// 노쇠지수 탭의 "권장 운동" 블록. AI 브리핑 아래에 둔다. 2026-10-15 사용자 결정: 순서가 있는 하루 운동 한 바퀴가 중심이다.
// 2026-10-20 사용자 결정: 외부 프로그램(Vivifrail)의 PDF 링크·그림·여권 유형은 라이선스 위반이라 쓰지 않는다 → 자체 "스마트 운동 등급"(1 보호~4 유지),
//   자체 SVG 고리(wheelSvg.js), 자체 그림(public/frail/exercises). 바깥 링크 없음.
// 2026-10-21 담당자 요청: 미리보기는 "운동 추천 화면" 팝업으로, 보내기 버튼은 그 팝업 안에. AI 보조 안내는 "운동/식단 추천 이유" 버튼(고리 옆)을 누르면 팝업.
//
//   머리(단계 배지·운동 등급·다시 만들기)
//   → (레드 플래그가 있으면) 고정 안내 문구
//   → 하루 운동 순서 고리(SVG) + 옆에 "운동/식단 추천 이유" 버튼 + 번호 순서의 운동 7~9개(자체 그림·횟수).
//     걷기·휴식·호흡 글은 따로 적지 않는다(2026-10-20 담당자). 안전 모드면 "낙상 주의형" 표시와 대체된 운동 표시.
//   → 보내기: 한마디 입력 + "보낼 화면 미리보기" → 팝업(사용자가 받는 페이지 iframe, 서버 sharePage.js) 안의 "운동 추천 보내기" → 사용자 앱 푸시 + 링크.
//     보낸 상태·열람 횟수는 블록에 남는다.
//   → "운동/식단 추천 이유" 팝업(선택 근거·식단·주의·케어매니저 한 줄) — 케어매니저만 본다. 사용자에게는 보내지 않는다.
//
// 프로그램·운동 목록은 카탈로그를 그대로 옮긴 것(서버 rules.programPlan)이고 모델은 끼지 않는다. 여기서 고르거나 바꾸지 않는다.
// ⚠️ 레드 플래그 문구는 모델이 아니라 서버 규칙(rules.js)이 붙인 것이다. 맨 위에, 지우지 않는다.

import React, { useEffect, useState } from 'react';
import { Dumbbell, Utensils, AlertTriangle, RefreshCw, PlayCircle, Sparkles, Send, ExternalLink, Eye, X } from 'lucide-react';

// 서버와 같은 CommonJS 모듈. 웹팩(CRA 빌드)은 CJS 모듈을 import 로 가져오면 거부하므로 require 로 받는다(테스트의 babel 은 import 도 통과해 못 잡는다 — 바꾸면 CI=true npm run build 로 확인).
const { renderWheelSvg } = require('../server/frail/wheelSvg');

const CATEGORY_NAME = { WARMUP: '준비', STRENGTH: '근력', BALANCE: '균형', AEROBIC: '걷기(유산소)', FLEXIBILITY: '유연·정리' };
const CATEGORY_ORDER = ['STRENGTH', 'BALANCE', 'AEROBIC', 'FLEXIBILITY', 'WARMUP'];
const STAGE_TONE = { MAINTAIN: 'normal', PREVENT: 'caution', INTENSIVE: 'danger', PROTECT: 'danger' };
const SOURCE_NAME = {
    'MOHW-PA-2023': '한국인을 위한 신체활동 지침서 2023', 'KDRI-2025': '2025 한국인 영양소 섭취기준', 'MOHW-NONMED-2022': '비의료 건강관리서비스 가이드라인 2022',
    'KHEPI-2023': '어·운·완 프로그램 운영 매뉴얼 2023', KJFP: '일차의료 노쇠 임상진료지침 2021', VIVIFRAIL: '다요소 운동 프로그램 문헌(Izquierdo 2017)', 'WHO-ICOPE-2017': 'WHO ICOPE 2017', 'WHO-ICOPE-2019': 'WHO ICOPE 2019'
};
const SEND_RESULT_TEXT = {
    sent: '사용자 앱으로 보냈습니다.',
    dry_run: 'dry-run 설정이라 실제로 보내지 않았습니다. 링크와 알림 이력만 만들었습니다.',
    no_token: '앱 푸시 토큰이 없어 보내지 못했습니다. 링크는 만들어졌습니다.',
    failed: '푸시 발송에 실패했습니다. 잠시 후 다시 시도해 주세요.'
};
const MESSAGE_MAX = 200;
const RATIONALE_TITLE = '운동/식단 추천 이유';   // 2026-10-21 담당자 문구

const sourcesText = (codes) => (codes || []).map((c) => SOURCE_NAME[c] || c).join(' · ');

const groupExercises = (exercises) => {
    const groups = new Map();
    for (const e of exercises) {
        if (!groups.has(e.category)) groups.set(e.category, []);
        groups.get(e.category).push(e);
    }
    return [...groups.entries()].sort((a, b) => CATEGORY_ORDER.indexOf(a[0]) - CATEGORY_ORDER.indexOf(b[0]));
};

/** 공용 .sds-modal 껍데기(GuardianControlCenter 와 같은 모양). 바탕을 누르거나 Esc 로 닫는다. */
const Modal = ({ title, onClose, className = '', wide = false, children, foot = null }) => {
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);
    return (
        <div className="sds-modal-overlay" onClick={onClose}>
            <div className={`sds-modal${wide ? ' sds-modal--wide' : ''} ${className}`.trim()} role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
                <div className="sds-modal__head">
                    <h2 className="sds-modal__title">{title}</h2>
                    <button type="button" className="sds-btn sds-btn--ghost sds-btn--sm sds-btn--icon" onClick={onClose} aria-label="닫기">
                        <X size={16} aria-hidden="true" />
                    </button>
                </div>
                <div className="sds-modal__body">{children}</div>
                {foot && <div className="sds-modal__foot">{foot}</div>}
            </div>
        </div>
    );
};

// 하루 운동 순서 고리는 서버와 같은 함수(wheelSvg.js)로 그린다 — 우리 운동 이름·분류 색뿐이고 글은 이스케이프된다.
const ProgramSection = ({ program, safeMode, onShowRationale = null }) => {
    const svg = renderWheelSvg({ items: program.items.map((it) => ({ no: it.no, name: it.name, category: it.category })), title: `등급 ${program.level}`, subtitle: `${program.levelName || ''} · 하루 한 바퀴`, size: 320 });
    return (
        <div className="detail-page__frail-wheel">
            <div className="detail-page__frail-wheel-hero">
                {/* eslint-disable-next-line react/no-danger */}
                <div className="detail-page__frail-wheel-img" dangerouslySetInnerHTML={{ __html: svg }} />
                {onShowRationale && (
                    <div className="detail-page__frail-wheel-aside">
                        <button type="button" className="sds-btn sds-btn--ghost sds-btn--sm" onClick={onShowRationale} title="케어매니저용 — 사용자에게는 보내지 않습니다">
                            <Sparkles size={14} aria-hidden="true" />{RATIONALE_TITLE}
                        </button>
                    </div>
                )}
            </div>
            {safeMode && (
                <small className="detail-page__frail-guide-safe detail-page__frail-wheel-safe">낙상 주의형: 잡고·앉아서 하는 방식으로 안내하고, 서서 하는 균형 동작은 대체했습니다.</small>
            )}
            <ol className="detail-page__frail-wheel-items" aria-label="운동 순서">
                {program.items.map((it) => (
                    <li key={it.no} title={it.description}>
                        {it.imageUrl
                            ? <img src={it.imageUrl} alt={`${it.name} 그림`} loading="lazy" />
                            : <span className="detail-page__frail-wheel-noimg" aria-hidden="true" />}
                        <span className="detail-page__frail-wheel-name">{it.no}. {it.name}{it.swapped ? ' (대체)' : ''}</span>
                        <small>{it.dose}</small>
                        {safeMode && it.safeMode && <small className="detail-page__frail-guide-safe">{it.safeMode}</small>}
                    </li>
                ))}
            </ol>
        </div>
    );
};

/** 운동/식단 추천 근거 팝업 본문(모델이 고른 근거·식단·주의·케어매니저 한 줄). 케어매니저만 본다. */
const RationaleBody = ({ guide }) => (
    <div data-testid="frail-guide-ai">
        {guide.rationale && <p className="detail-page__frail-brief-summary">{guide.rationale}</p>}

        {groupExercises(guide.exercises).map(([category, list]) => (
            <div key={category} className="detail-page__frail-brief-group">
                <b><Dumbbell size={12} aria-hidden="true" /> {CATEGORY_NAME[category] || category}</b>
                {list.map((e) => (
                    <div className="sds-list__row detail-page__frail-guide-row" key={e.id}>
                        {e.imageUrl && (
                            <span className="detail-page__frail-guide-thumb">
                                <img src={e.imageUrl} alt={`${e.name} 그림`} loading="lazy" />
                            </span>
                        )}
                        <div className="sds-list__body">
                            <b title={sourcesText(e.sources)}>{e.name}</b>
                            <small>{e.doseText}</small>
                            {e.why && <small>{e.why}</small>}
                            {guide.safeMode && e.safeMode && <small className="detail-page__frail-guide-safe">안전 방식: {e.safeMode}</small>}
                            {e.caution && <small className="detail-page__frail-guide-caution">{e.caution}</small>}
                        </div>
                        <div className="sds-list__spacer" />
                        {e.videoUrl && (
                            <a className="sds-btn sds-btn--ghost sds-btn--sm" href={e.videoUrl} target="_blank" rel="noreferrer">
                                <PlayCircle size={14} aria-hidden="true" />영상
                            </a>
                        )}
                    </div>
                ))}
            </div>
        ))}

        {guide.nutrition.length > 0 && (
            <div className="detail-page__frail-brief-group">
                <b><Utensils size={12} aria-hidden="true" /> 식단</b>
                {guide.nutrition.map((n) => (
                    <div className="sds-list__row detail-page__frail-guide-row" key={n.id}>
                        <div className="sds-list__body">
                            <b title={sourcesText(n.sources)}>{n.name}</b>
                            <small>{n.userText}</small>
                            {n.why && <small>{n.why}</small>}
                            {n.caution && <small className="detail-page__frail-guide-caution">{n.caution}</small>}
                        </div>
                    </div>
                ))}
            </div>
        )}
        {guide.exclusions && guide.exclusions.length > 0 && (
            <p className="detail-page__frail-guide-caution">병력 때문에 일부 식단 항목을 뺐습니다. 담당 의료진의 식이 지침이 우선입니다.</p>
        )}

        {guide.cautions.length > 0 && (
            <div className="detail-page__frail-brief-group">
                <b>주의</b>
                <ul>{guide.cautions.map((t, i) => <li key={`c${i}`}>{t}</li>)}</ul>
            </div>
        )}
        {guide.forCareManager && <p className="detail-page__frail-brief-closing">{guide.forCareManager}</p>}
        {guide.chronicNotice && <p className="detail-page__frail-guide-chronic">{guide.chronicNotice}</p>}
    </div>
);

/**
 * 보내기. 한마디를 적고 "보낼 화면 미리보기" 를 누르면 팝업에 사용자가 받을 페이지가 뜨고, 보내기 버튼은 그 팝업 안에 있다(2026-10-21 담당자).
 * onPreview 가 없으면(미리보기 API 없음) 보내기 버튼을 블록에 바로 둔다.
 */
const SendPanel = ({ sent, onSend, onPreview = null, sending, sendResult }) => {
    const [message, setMessage] = useState('');
    const [preview, setPreview] = useState(null); // { html } | { error }
    const [previewing, setPreviewing] = useState(false);
    useEffect(() => {
        if (sendResult && sendResult.status !== 'error') setPreview(null);   // 보내고 나면 팝업을 닫는다
    }, [sendResult]);
    if (!onSend) return null;
    const sendLabel = sent ? '다시 보내기' : '운동 추천 보내기';
    const showPreview = async () => {
        if (!onPreview || previewing) return;
        setPreviewing(true);
        try {
            const html = await onPreview(message.trim() || null);
            setPreview(html ? { html } : { error: '미리보기를 받지 못했습니다.' });
        } catch (e) {
            setPreview({ error: e?.response?.data?.error?.message || e?.message || '미리보기를 받지 못했습니다.' });
        } finally {
            setPreviewing(false);
        }
    };
    const sendButton = (
        <button type="button" className="sds-btn sds-btn--primary sds-btn--sm" onClick={() => onSend(message.trim() || null)} disabled={sending}>
            <Send size={14} aria-hidden="true" />{sending ? '보내는 중…' : sendLabel}
        </button>
    );
    return (
        <div className="detail-page__frail-send">
            {sent && (
                <p className="detail-page__frail-send-state">
                    {sent.sentAt} 보냄{sent.sentCount > 1 ? ` (${sent.sentCount}회)` : ''}
                    {' · '}열람 {sent.viewCount || 0}회{sent.lastViewedAt ? ` (마지막 ${sent.lastViewedAt})` : ''}
                    {sent.expiresOn ? ` · 링크 만료 ${sent.expiresOn}` : ''}
                    {sent.url && <a href={sent.url} target="_blank" rel="noreferrer"><ExternalLink size={12} aria-hidden="true" /> 사용자 페이지</a>}
                </p>
            )}
            <div className="detail-page__frail-send-form">
                <label>
                    <span>대상자에게 전할 한마디 (선택, {MESSAGE_MAX}자)</span>
                    <textarea value={message} maxLength={MESSAGE_MAX} rows={2} placeholder="예: 이번 주는 2번·4번부터 천천히 해 보세요." onChange={(e) => setMessage(e.target.value)} disabled={sending} />
                </label>
                <div className="detail-page__frail-send-actions">
                    {onPreview
                        ? (
                            <button type="button" className="sds-btn sds-btn--primary sds-btn--sm" onClick={showPreview} disabled={previewing || sending}>
                                <Eye size={14} aria-hidden="true" />{previewing ? '불러오는 중…' : '보낼 화면 미리보기'}
                            </button>
                        )
                        : sendButton}
                </div>
            </div>
            {sendResult && (
                <p className={`detail-page__frail-send-result detail-page__frail-send-result--${sendResult.status}`} role="status">
                    {SEND_RESULT_TEXT[sendResult.status] || `보내지 못했습니다. ${sendResult.error || ''}`.trim()}
                </p>
            )}
            {preview && (
                <Modal
                    title="운동 추천 화면" wide className="detail-page__frail-modal-preview" onClose={() => setPreview(null)}
                    foot={(
                        <>
                            <p>검토한 맞춤 운동 프로그램(등급·시작하기 전에·주의사항·운동 순서와 그림·설명·생활 권고)을 대상자 앱으로 보냅니다.</p>
                            {!preview.error && sendButton}
                            <button type="button" className="sds-btn sds-btn--ghost sds-btn--sm" onClick={() => setPreview(null)} disabled={sending}>닫기</button>
                        </>
                    )}
                >
                    {preview.error
                        ? <p className="detail-page__frail-send-result detail-page__frail-send-result--error" role="alert">{preview.error}</p>
                        : <iframe title="사용자 페이지 미리보기" sandbox="allow-popups allow-popups-to-escape-sandbox" srcDoc={preview.html} />}
                </Modal>
            )}
        </div>
    );
};

const FrailGuideBlock = ({ guide, status, error, createdAt, cached = false, onRegenerate, available, sent = null, onSend = null, onPreview = null, sending = false, sendResult = null }) => {
    const [showRationale, setShowRationale] = useState(false);
    if (!available) return null;
    const program = status === 'ready' && guide ? guide.program : null;
    return (
        <div className="detail-page__frail-guide">
            <div className="detail-page__frail-brief-head">
                <span className="detail-page__insights-badge"><Dumbbell size={12} aria-hidden="true" /> 권장 운동 · 맞춤 운동 프로그램</span>
                {status === 'ready' && guide && (
                    <span className={`sds-badge sds-badge--${STAGE_TONE[guide.stage] || 'none'}`}>
                        <span>{guide.stageName} 단계</span>
                    </span>
                )}
                {status === 'ready' && (guide?.level || program) && (
                    <small className="detail-page__frail-brief-when">스마트 운동 등급 {guide?.level?.level || program?.level}{guide?.safeMode ? ' · 낙상 주의형' : ''}</small>
                )}
                {status === 'ready' && createdAt && <small className="detail-page__frail-brief-when">{createdAt} 작성{cached ? '' : ' (새로 만듦)'}</small>}
                {status === 'ready' && onRegenerate && (
                    <button type="button" className="sds-btn sds-btn--ghost sds-btn--sm detail-page__frail-brief-refresh" onClick={onRegenerate}>
                        <RefreshCw size={14} aria-hidden="true" />다시 만들기
                    </button>
                )}
            </div>
            {status === 'loading' && <p className="sds-list__empty">등급에 맞는 운동 프로그램과 보조 안내를 준비하는 중입니다. 처음에는 30초쯤 걸릴 수 있습니다.</p>}
            {status === 'failed' && (
                <p className="sds-list__empty">
                    권장 운동을 만들지 못했습니다.{error ? ` (${error})` : ''}
                    {onRegenerate && <button type="button" className="sds-btn sds-btn--ghost sds-btn--sm" onClick={onRegenerate}>다시 만들기</button>}
                </p>
            )}
            {status === 'disabled' && <p className="sds-list__empty">권장 운동이 꺼져 있습니다.</p>}
            {status === 'ready' && guide && (
                <>
                    {guide.notices.length > 0 && (
                        <div className="detail-page__frail-guide-notices" role="alert">
                            {guide.notices.map((n) => (
                                <div key={n.code} className="detail-page__frail-guide-notice">
                                    <AlertTriangle size={16} aria-hidden="true" />
                                    <div><b>{n.name}</b><p>{n.text}</p></div>
                                </div>
                            ))}
                        </div>
                    )}

                    {program
                        ? <ProgramSection program={program} safeMode={guide.safeMode} onShowRationale={() => setShowRationale(true)} />
                        : (
                            <p className="sds-list__empty">
                                이 가이드에는 운동 프로그램이 없습니다. 다시 만들면 붙습니다.
                                <button type="button" className="sds-btn sds-btn--ghost sds-btn--sm" onClick={() => setShowRationale(true)}><Sparkles size={14} aria-hidden="true" />{RATIONALE_TITLE}</button>
                            </p>
                        )}

                    <SendPanel sent={sent} onSend={program ? onSend : null} onPreview={program ? onPreview : null} sending={sending} sendResult={sendResult} />

                    {showRationale && (
                        <Modal title={RATIONALE_TITLE} wide className="detail-page__frail-modal-rationale" onClose={() => setShowRationale(false)}>
                            <RationaleBody guide={guide} />
                        </Modal>
                    )}
                </>
            )}
        </div>
    );
};

export default FrailGuideBlock;
